import { memo, useState, useEffect, useRef } from 'react';
import { BarChart3, Globe, Users, Layers, DollarSign, GitMerge } from 'lucide-react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { usePortfolioData } from './usePortfolioData';

// Cache last-known values so the count animation always starts from a sensible baseline
const lastKnownValues = {};

function useAnimatedValue(target, duration = 800, initialValue = 0) {
  const [display, setDisplay] = useState(initialValue);
  const currentRef = useRef(initialValue);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = currentRef.current;
    if (from === target) { setDisplay(target); return; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const start = performance.now();
    function step(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = Math.round(from + (target - from) * eased);
      currentRef.current = val;
      setDisplay(val);
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

const KpiCard = memo(function KpiCard({ icon: Icon, label, value, color, subtitle }) {
  const initialValue = lastKnownValues[label] ?? 0;
  const displayValue = useAnimatedValue(value, 800, initialValue);

  useEffect(() => { lastKnownValues[label] = value; }, [label, value]);

  return (
    <div
      className="flex-1 min-w-[130px] p-4 rounded-xl bg-[var(--surface-secondary)] border border-[var(--border-primary)] text-center"
    >
      <div className="flex justify-center mb-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}18` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)] tabular-nums">
        {displayValue.toLocaleString()}
      </div>
      <div className="text-xs text-[var(--text-tertiary)] mt-1 font-medium">{label}</div>
      {subtitle && (
        <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5 opacity-70">{subtitle}</div>
      )}
    </div>
  );
});

/**
 * PortfolioKpiStrip
 * Displays key portfolio-wide metrics as a horizontal strip of animated KPI cards:
 * Total Results, Programs with results, Countries, Partners, W1/W2 count, W3/Bilateral count.
 */
export const PortfolioKpiStrip = memo(function PortfolioKpiStrip() {
  const {
    filteredResults,
    programBreakdown,
    totalResults,
    totalCountries,
    totalPartners,
    fundingBreakdown,
  } = usePortfolioData();

  if (!filteredResults || filteredResults.length === 0) return null;

  const programsWithResults = (programBreakdown || []).filter(p => p.count > 0).length;
  const w1w2Count = fundingBreakdown?.find(f => f.key === 'w1w2')?.count ?? 0;
  const w3Count = fundingBreakdown?.find(f => f.key === 'w3bilateral')?.count ?? 0;

  const metrics = [
    {
      icon: BarChart3,
      label: 'Total Results',
      value: totalResults,
      color: '#2d7f3f',
    },
    {
      icon: Layers,
      label: 'Programs',
      value: programsWithResults,
      color: '#007dbc',
      subtitle: 'with at least 1 result',
    },
    {
      icon: Globe,
      label: 'Countries',
      value: totalCountries,
      color: '#f37021',
    },
    {
      icon: Users,
      label: 'Partners',
      value: totalPartners,
      color: '#8B5CF6',
    },
    {
      icon: DollarSign,
      label: 'W1/W2 Pooled',
      value: w1w2Count,
      color: '#2d7f3f',
    },
    {
      icon: GitMerge,
      label: 'W3/Bilateral',
      value: w3Count,
      color: '#f37021',
    },
  ];

  const chartData = metrics.map(m => ({ Metric: m.label, Value: m.value }));

  return (
    <ChartWrapper
      title="Portfolio-Wide Key Metrics"
      info="Summary of key metrics across all programs in the current filtered selection. Counts update when you change filters."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="portfolio-kpi-summary"
    >
      <div className="flex flex-wrap gap-3 justify-center">
        {metrics.map(m => (
          <KpiCard key={m.label} {...m} />
        ))}
      </div>
      {w1w2Count > 0 && w3Count > 0 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <div className="h-2.5 rounded-full overflow-hidden flex" style={{ width: 200 }}>
            <div
              style={{
                width: `${(w1w2Count / (w1w2Count + w3Count)) * 100}%`,
                backgroundColor: '#2d7f3f',
              }}
            />
            <div
              style={{
                width: `${(w3Count / (w1w2Count + w3Count)) * 100}%`,
                backgroundColor: '#f37021',
              }}
            />
          </div>
          <span className="text-xs text-[var(--text-tertiary)]">
            {((w1w2Count / (w1w2Count + w3Count)) * 100).toFixed(0)}% W1/W2
            &nbsp;/&nbsp;
            {((w3Count / (w1w2Count + w3Count)) * 100).toFixed(0)}% W3
          </span>
        </div>
      )}
    </ChartWrapper>
  );
});
